import {
  ForwardRefRenderFunction,
  ReactElement,
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
} from 'react'

import { CommonGAssets } from '@hhgtech/hhg-components/gAssets'
import { ButtonProps, Modal, ModalProps } from '@hhgtech/hhg-components/mantine'
import { BoxProps, Transition } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import dynamic from 'next/dynamic'

import { CarePathDefaultContext } from 'components/templates/carePath/landing/context'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { MANTINE_MODAL_TARGET } from 'interfaces/constants'
import { SpotlightLeadgenSession } from 'interfaces/types'
// import { fastdom } from 'utils/fastdom'

import { Body } from './body'
import { LeadgenWithBannerContext } from './context'
import { DrawerModal } from './drawerModal'
import useStyles from './index.styles'
import { HeroBannerProps } from '../heroBanner'
import SectionWrapper from '../sectionWrapper'

export type ModalType = 'normal' | 'fullscreen' | 'drawer'

export type LeadgenWithBannerProps = {
  data: SpotlightLeadgenSession
  primaryColor?: string
  isModal?: boolean
  modalStyle?: 'normal' | 'fullscreen'
  desktopModalType?: ModalType
  mobileModalType?: ModalType
  isOpened?: boolean
  modalBanner?: ReactElement<HeroBannerProps>
  scrollable?: boolean
  hashToOpenModal?: string
  onDone?: () => void
  autoOpen?: boolean
  modalProps?: Partial<ModalProps>
  closeBtnProps?: Partial<ButtonProps>
  submitBtnProps?: Partial<ButtonProps>
  onModalChange?: (state: boolean) => void
  onCloseForm?: () => void
} & BoxProps

export type LeadgenWithBannerHandlerProps = {
  openModal: () => void
  closeModal: () => void
}

const LeadgenBody = dynamic(
  () => import('./leadgenBody').then((res) => res.LeadgenBody),
  {
    ssr: false,
  },
)

const LeadgenWithBanner: ForwardRefRenderFunction<
  LeadgenWithBannerHandlerProps,
  LeadgenWithBannerProps
> = (
  {
    data,
    isModal,
    modalStyle = 'fullscreen',
    desktopModalType = 'normal',
    mobileModalType = 'fullscreen',
    onCloseForm,
    modalBanner,
    primaryColor,
    styles,
    className,
    classNames,
    hashToOpenModal = 'open-lead',
    onDone,
    autoOpen,
    scrollable,
    closeBtnProps,
    submitBtnProps,
    // onModalChange,
  },
  ref,
) => {
  const { detectPage } = useContext(CarePathDefaultContext)
  const { isSoya, isTiki } = detectPage
  const isMobile = useIsMobile()

  const modalType = isMobile ? mobileModalType : desktopModalType
  const isFullscreenModal = modalType === 'fullscreen'
  const { campaigns, backgroundColor, isSkippable } = data
  const { classes, cx, theme } = useStyles(
    { bgColor: backgroundColor },
    {
      name: 'LeadgenWithBanner',
      styles,
      classNames,
    },
  )
  const router = useRouterExtend()
  const { asPath } = router
  const [opened, { open, close }] = useDisclosure(false)

  useImperativeHandle(ref, () => ({
    openModal: open,
    closeModal: close,
    isOpened: opened,
  }))

  useEffect(() => {
    if (isModal && autoOpen && (campaigns?.rules || [])) {
      const times =
        (Number(
          (campaigns?.rules || []).filter(
            (i: { rule: string; value: string }) => i.rule === 'time',
          )?.[0]?.value,
        ) || 30) * 1000
      setTimeout(() => {
        open()
      }, times)
    }
  }, [campaigns])

  useEffect(() => {
    if (isSoya && isModal) {
      const hash = (window.location.hash || '')
        .substring(1)
        .toLowerCase()
        .split('?')?.[0]

      hash.includes(hashToOpenModal) ? open() : close()
    }
  }, [asPath])

  useEffect(() => {
    if (isModal && isSoya) {
      const onhashchange = () => {
        window.location.hash === `#${hashToOpenModal}` ? open() : close()
      }
      window.addEventListener('hashchange', onhashchange, { passive: true })
      return () => window.removeEventListener('hashchange', onhashchange)
    }
  }, [])

  const bodyClassNames = {
    root: classes.wrapper,
    formFrame: classes.formFrame,
    bannerImg: classes.bannerImg,
    container: classes.container,
  }

  return isSoya || (!isSoya && campaigns) ? (
    <LeadgenWithBannerContext.Provider
      value={{
        modalBanner,
        onDone,
        modalStyle,
        isModal,
        data,
        primaryColor,
        close,
        onCloseForm,
        isFullscreenModal,
        modalType,
        scrollable,
        closeBtnProps,
        submitBtnProps,
      }}
    >
      {isModal ? (
        isSoya ? (
          modalType === 'drawer' ? (
            <DrawerModal opened={opened} />
          ) : (
            <Modal
              target={MANTINE_MODAL_TARGET}
              opened={opened}
              closeOnClickOutside={isSkippable}
              fullScreen={desktopModalType === 'fullscreen'}
              zIndex={302}
              size={isTiki ? 960 : undefined}
              classNames={{
                body: classes.modalBody,
              }}
              transitionProps={{ transition: 'pop' }}
              styles={{
                header: {
                  border: 0,
                  paddingBottom: 0,
                  display: 'none',
                },
                body: {
                  padding: 0,
                  height: '100%',
                  ...(isTiki && {
                    backgroundImage:
                      'linear-gradient(139.59deg, #182EF3 2.87%, #021575 89.05%)',
                    position: 'relative',
                    [theme.fn.largerThan('sm')]: {
                      height: 'auto',
                      '&:before,&:after': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 0,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'top',
                      },
                      '&:before': {
                        backgroundImage: `url(${CommonGAssets.getAssetPath(
                          'tiki/top-bg.png',
                        )})`,
                        backgroundSize: 'contain',
                      },
                      '&:after': {
                        backgroundImage: `url(${CommonGAssets.getAssetPath(
                          'tiki/icon-bg-modal.png',
                        )})`,
                        backgroundSize: 'contain',
                        backgroundPosition: 'left center',
                      },
                    },
                    '.mantine-LeadgenWithBanner-wrapper': {
                      position: 'relative',
                      zIndex: 1,
                    },
                  }),
                },
                inner: {
                  height: '100%',
                },
                content: {
                  height: isTiki ? 'auto' : '100% !important',
                  ...(isFullscreenModal && {
                    borderRadius: 0,
                  }),
                },
              }}
              onClose={() => {
                // setShowSuccess(false)
                // onDone?.()
              }}
            >
              <Body
                classNames={bodyClassNames}
                scrollHeight={480}
                onDone={() => {
                  close()
                  onDone?.()
                }}
              />
            </Modal>
          )
        ) : (
          <Transition mounted={opened} transition={'fade'}>
            {(styles) => (
              <div style={styles}>
                <LeadgenBody
                  onDone={() => {
                    close()
                    onDone?.()
                  }}
                />
              </div>
            )}
          </Transition>
        )
      ) : (
        <SectionWrapper.Wrapper
          className={cx(className, classes.root)}
          data-inline="true"
        >
          <Body classNames={bodyClassNames} />
        </SectionWrapper.Wrapper>
      )}
    </LeadgenWithBannerContext.Provider>
  ) : null
}
export default forwardRef(LeadgenWithBanner)
